import { Alert, Spin } from 'antd';
import moment from 'moment';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import Routes from '../../config/routes';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { regulationGoalColumns, timingSnapshotFromMatch } from '../../helpers/matchTiming';
import {
    EventLightVm,
    EventsClient,
    MatchDetailVm,
    MatchesClient,
    PlayerPositionEnum,
} from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages, insightsScoreText } from './insightsAccess';
import { useInsightsClub } from './insightsClubScope';
import { buildGameStateSplits, GameStateSplits, shootPct } from './insightsGameState';
import InsightsLegend from './InsightsLegend';
import {
    buildMatchPeriodDetails,
    buildMatchSituationPanel,
    buildMatchTimelineGroups,
    MatchPeriodDetail,
    MatchSituationRow,
} from './insightsMatchAnalysis';
import InsightsMatchMaps from './InsightsMatchMaps';
import { buildMatchReport, buildMomentumSeries } from './insightsMatchMomentum';
import {
    buildPlayerStats,
    buildScoreProgress,
    buildTeamStats,
    PlayerMatchStats,
    shootingPercent,
    TeamMatchStats,
} from './insightsMatchStats';
import InsightsMomentumChart from './InsightsMomentumChart';
import InsightsTopbar from './InsightsTopbar';
import { makeRateCell, TwOtherBoard, TwSavesBoard, TwScorersBoard } from './InsightsTwBoards';
import { buildTwPlayerBoards, TwPlayerBoardRow } from './insightsTwPlayerTables';
import ScoreEvolutionChart from './ScoreEvolutionChart';

interface RouteParams {
    matchId: string;
}

interface Props extends RouteComponentProps<RouteParams> {
    userProfile?: Profile;
}

const periodCells = (match: MatchDetailVm): { key: string; home?: number; away?: number }[] => {
    const columns = regulationGoalColumns(match, timingSnapshotFromMatch(match).periodCount);
    return [
        ...columns.map((column) => ({
            key: column.label,
            home: column.home ?? undefined,
            away: column.away ?? undefined,
        })),
        { key: 'OT', home: match.homeTeamGoalsOT, away: match.awayTeamGoalsOT },
    ];
};

const InsightsMatch: React.FC<Props> = ({ userProfile, match: routeMatch }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);
    const matchId = parseInt(routeMatch.params.matchId, 10);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { canSwitchClubs, clubs, selectedClubId, selectClub } = scope;

    const [detail, setDetail] = useState<MatchDetailVm | undefined>();
    const [events, setEvents] = useState<EventLightVm[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (!allowed || !canUseInsightsPages(userRole) || Number.isNaN(matchId)) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const [matchDetail, lightEvents] = await Promise.all([
                    new MatchesClient().getById(matchId),
                    new EventsClient().getLightByMatchId(matchId),
                ]);
                if (cancelled) return;
                setDetail(matchDetail);
                setEvents(lightEvents || []);
            } catch {
                if (!cancelled) {
                    setDetail(undefined);
                    setEvents([]);
                    setError('Could not load this match. It may be outside your access scope.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, matchId]);

    const matchClubIds = useMemo(
        () =>
            [detail?.homeTeam?.clubId, detail?.awayTeam?.clubId].filter(
                (clubId): clubId is number => clubId != null
            ),
        [detail]
    );

    /*
     * A deep link can point at a match of another club. Admin and federation users get
     * switched over silently; a club user keeps their own club and sees the notice below.
     */
    useEffect(() => {
        if (!canSwitchClubs || selectedClubId == null || matchClubIds.length === 0) return;
        if (matchClubIds.includes(selectedClubId)) return;

        const target = matchClubIds.find((clubId: number) =>
            clubs.some((club) => club.id === clubId)
        );
        if (target != null) selectClub(target);
    }, [canSwitchClubs, clubs, matchClubIds, selectedClubId, selectClub]);

    const outsideSelectedClub =
        detail != null &&
        selectedClubId != null &&
        matchClubIds.length > 0 &&
        !matchClubIds.includes(selectedClubId);

    const playerStats = useMemo(
        () => (detail ? buildPlayerStats(detail, events) : []),
        [detail, events]
    );
    const teamStats = useMemo(
        () => (detail ? buildTeamStats(detail, events) : undefined),
        [detail, events]
    );
    const scoreProgress = useMemo(
        () => (detail ? buildScoreProgress(detail, events) : []),
        [detail, events]
    );

    // Maps are drawn from the home side's perspective when the match is not ours.
    const perspectiveTeamIds = useMemo(() => {
        if (!detail) return [];
        if (selectedClubId != null && detail.awayTeam?.clubId === selectedClubId) {
            return [detail.awayTeamId];
        }
        if (selectedClubId != null && detail.homeTeam?.clubId === selectedClubId) {
            return [detail.homeTeamId];
        }
        return [detail.homeTeamId];
    }, [detail, selectedClubId]);

    const mapBundle = useMemo(() => (detail ? [{ detail, events }] : []), [detail, events]);

    const situationPanel = useMemo(
        () =>
            mapBundle.length > 0
                ? buildMatchSituationPanel(mapBundle[0], perspectiveTeamIds)
                : undefined,
        [mapBundle, perspectiveTeamIds]
    );

    const periodDetails = useMemo(
        () =>
            mapBundle.length > 0 ? buildMatchPeriodDetails(mapBundle[0], perspectiveTeamIds) : [],
        [mapBundle, perspectiveTeamIds]
    );

    const playerNameByMatchPlayerId = useMemo(() => {
        const map = new Map<number, string>();
        playerStats.forEach((row: PlayerMatchStats) => {
            map.set(row.matchPlayerId, `#${row.capNumber} ${row.name}`);
        });
        return map;
    }, [playerStats]);

    const timelineGroups = useMemo(
        () =>
            mapBundle.length > 0
                ? buildMatchTimelineGroups(mapBundle[0], playerNameByMatchPlayerId)
                : [],
        [mapBundle, playerNameByMatchPlayerId]
    );

    const ourTheirGoals = useMemo(() => {
        if (!detail || perspectiveTeamIds.length === 0) return { ourGoals: 0, theirGoals: 0 };
        const ourId = perspectiveTeamIds[0];
        const ourGoals =
            ourId === detail.homeTeamId
                ? detail.homeTeamGoalsTotal ?? teamStats?.home.goals ?? 0
                : detail.awayTeamGoalsTotal ?? teamStats?.away.goals ?? 0;
        const theirGoals =
            ourId === detail.homeTeamId
                ? detail.awayTeamGoalsTotal ?? teamStats?.away.goals ?? 0
                : detail.homeTeamGoalsTotal ?? teamStats?.home.goals ?? 0;
        return { ourGoals, theirGoals };
    }, [detail, perspectiveTeamIds, teamStats]);

    const matchReport = useMemo(() => {
        if (mapBundle.length === 0 || perspectiveTeamIds.length === 0) return undefined;
        return buildMatchReport(
            mapBundle[0],
            perspectiveTeamIds,
            ourTheirGoals.ourGoals,
            ourTheirGoals.theirGoals
        );
    }, [mapBundle, perspectiveTeamIds, ourTheirGoals]);

    const momentum = useMemo(
        () => (mapBundle.length > 0 ? buildMomentumSeries(mapBundle[0], perspectiveTeamIds) : []),
        [mapBundle, perspectiveTeamIds]
    );

    const gameState: GameStateSplits | undefined = useMemo(
        () =>
            mapBundle.length > 0
                ? buildGameStateSplits(mapBundle[0], perspectiveTeamIds)
                : undefined,
        [mapBundle, perspectiveTeamIds]
    );

    const twBoards = useMemo(
        () =>
            detail && perspectiveTeamIds.length > 0
                ? buildTwPlayerBoards([{ detail, events }], perspectiveTeamIds)
                : [],
        [detail, events, perspectiveTeamIds]
    );

    // Match boards show the full our-club lineup (zeros included). Saves also
    // includes listed / swapped GKs with no SOG faced yet.
    const scorers = twBoards;
    const otherRows = twBoards;
    const keepers = useMemo(() => {
        if (!detail) return [];
        const lineupByMpId = new Map(
            (detail.players || []).filter((mp) => mp.id != null).map((mp) => [mp.id as number, mp])
        );
        const gkPlayerIds = new Set<number>();
        (detail.players || []).forEach((mp) => {
            if (!perspectiveTeamIds.includes(mp.teamId) || mp.playerId == null) return;
            if (mp.positionId === PlayerPositionEnum.Goalkeeper) gkPlayerIds.add(mp.playerId);
        });
        events.forEach((event: EventLightVm) => {
            const change = event.goalkeeperChange;
            if (!change) return;
            [change.newGoalkeeperMatchPlayerId, change.previousGoalkeeperMatchPlayerId].forEach(
                (mpId) => {
                    if (mpId == null) return;
                    const mp = lineupByMpId.get(mpId);
                    if (mp && perspectiveTeamIds.includes(mp.teamId) && mp.playerId != null) {
                        gkPlayerIds.add(mp.playerId);
                    }
                }
            );
        });
        return twBoards.filter(
            (r: TwPlayerBoardRow) => r.saves.total.attempts > 0 || gkPlayerIds.has(r.playerId)
        );
    }, [detail, events, perspectiveTeamIds, twBoards]);
    const rateCell = useMemo(() => makeRateCell('totals'), []);

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    if (Number.isNaN(matchId)) {
        return (
            <div className="insightsRoot">
                <div className="insightsBody">
                    <Alert type="error" showIcon message="Invalid match id." />
                    <p style={{ marginTop: 12 }}>
                        <Link to={Routes.ROUTE_INSIGHTS}>Back to Insights</Link>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope}>
                <Link className="insightsBackLink" to={Routes.ROUTE_INSIGHTS}>
                    All matches
                </Link>
            </InsightsTopbar>

            <div className="insightsBody">
                <InsightsLegend />
                <Spin spinning={loading}>
                    {error && (
                        <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                    )}

                    {outsideSelectedClub && (
                        <Alert
                            type="warning"
                            showIcon
                            message="This match is outside the selected club"
                            description="You are viewing a match that neither team of the selected club played."
                            style={{ marginBottom: 14 }}
                        />
                    )}

                    {detail && (
                        <>
                            <section className="insightsAnalyseHero">
                                <div className="insightsAnalyseMeta">
                                    <Link className="insightsBackLink" to={Routes.ROUTE_INSIGHTS}>
                                        Insights
                                    </Link>
                                    <span className="insightsAnalyseDot">/</span>
                                    <span>{detail.competitionDisplayName}</span>
                                    {detail.startDate && (
                                        <span className="insightsAnalyseWhen">
                                            {moment(detail.startDate).format('DD MMM YYYY HH:mm')}
                                        </span>
                                    )}
                                </div>

                                <div className="insightsScoreboard">
                                    <div className="insightsScoreboardTeam isHome">
                                        <span className="insightsScoreboardLabel">Home</span>
                                        <strong>{detail.homeTeamDisplayName}</strong>
                                    </div>
                                    <div className="insightsScoreboardCenter">
                                        <div className="insightsScoreboardScore">
                                            {insightsScoreText(detail)}
                                        </div>
                                        {detail.status && (
                                            <span className="insightsStatus">{detail.status}</span>
                                        )}
                                    </div>
                                    <div className="insightsScoreboardTeam isAway">
                                        <span className="insightsScoreboardLabel">Away</span>
                                        <strong>{detail.awayTeamDisplayName || 'TBD'}</strong>
                                    </div>
                                </div>

                                <div className="insightsPeriodStrip" aria-label="Period scores">
                                    {periodCells(detail).map((cell) => (
                                        <div key={cell.key} className="insightsPeriodCell">
                                            <i>{cell.key}</i>
                                            <b>
                                                {cell.home ?? '-'}
                                                {':'}
                                                {cell.away ?? '-'}
                                            </b>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <div className="insightsMatchLeadRow">
                                {teamStats ? (
                                    <section className="insightsPanel">
                                        <div className="insightsPanelHead">
                                            <h2>Team comparison</h2>
                                            <span>From logged events</span>
                                        </div>
                                        <TeamComparison
                                            home={teamStats.home}
                                            away={teamStats.away}
                                            homeName={detail.homeTeamDisplayName}
                                            awayName={detail.awayTeamDisplayName || 'Away'}
                                        />
                                    </section>
                                ) : (
                                    <section className="insightsPanel">
                                        <div className="insightsPanelHead">
                                            <h2>Team comparison</h2>
                                        </div>
                                        <div className="insightsEmpty">No team stats yet.</div>
                                    </section>
                                )}

                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Score evolution</h2>
                                        <span>Hover a goal for details</span>
                                    </div>
                                    <ScoreEvolutionChart
                                        points={scoreProgress}
                                        homeName={detail.homeTeamDisplayName}
                                        awayName={detail.awayTeamDisplayName || 'Away'}
                                    />
                                </section>
                            </div>

                            {matchReport ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Match report</h2>
                                        <span>Auto summary from logged events</span>
                                    </div>
                                    <div className="insightsStatStrip">
                                        {matchReport.keyNumbers.map((n) => (
                                            <div key={n.label} className="insightsStat">
                                                <b>{n.value}</b>
                                                <i>{n.label}</i>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="insightsNarrativeStrip">
                                        <div className="insightsNarrativeCard isWell">
                                            <i>Went well</i>
                                            <b>{matchReport.narrative.well}</b>
                                        </div>
                                        <div className="insightsNarrativeCard isPoor">
                                            <i>Needs work</i>
                                            <b>{matchReport.narrative.poorly}</b>
                                        </div>
                                        <div className="insightsNarrativeCard isTurn">
                                            <i>Turning point</i>
                                            <b>{matchReport.narrative.turningPoint}</b>
                                        </div>
                                    </div>
                                    <div className="insightsReportSections">
                                        {matchReport.sections.map((section) => (
                                            <div
                                                key={section.title}
                                                className="insightsReportSection"
                                            >
                                                <h3>{section.title}</h3>
                                                <ul>
                                                    {section.lines.map((line) => (
                                                        <li key={line}>{line}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {momentum.length > 1 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Momentum</h2>
                                        <span>Our control score over the match</span>
                                    </div>
                                    <InsightsMomentumChart points={momentum} />
                                </section>
                            ) : null}

                            {(gameState &&
                                (gameState.leading.shots > 0 ||
                                    gameState.tied.shots > 0 ||
                                    gameState.trailing.shots > 0 ||
                                    gameState.clutch.shots > 0)) ||
                            situationPanel ? (
                                <div className="insightsTableCluster">
                                    {gameState &&
                                    (gameState.leading.shots > 0 ||
                                        gameState.tied.shots > 0 ||
                                        gameState.trailing.shots > 0 ||
                                        gameState.clutch.shots > 0) ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Game state</h2>
                                                <span>
                                                    Our shots by score · clutch = last 5′ &amp;
                                                    within 2
                                                </span>
                                            </div>
                                            <table className="insightsPlayerTable insightsTableFit">
                                                <thead>
                                                    <tr>
                                                        <th>State</th>
                                                        <th>Goals</th>
                                                        <th>Shots</th>
                                                        <th>%</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(
                                                        [
                                                            ['Leading', gameState.leading],
                                                            ['Tied', gameState.tied],
                                                            ['Trailing', gameState.trailing],
                                                            ['Clutch', gameState.clutch],
                                                        ] as const
                                                    ).map(([label, bucket]) => (
                                                        <tr key={label}>
                                                            <td>{label}</td>
                                                            <td>{bucket.goals}</td>
                                                            <td>{bucket.shots}</td>
                                                            <td>
                                                                {shootPct(bucket) == null
                                                                    ? '-'
                                                                    : `${shootPct(bucket)}%`}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </section>
                                    ) : null}

                                    {situationPanel ? (
                                        <section className="insightsPanel">
                                            <div className="insightsPanelHead">
                                                <h2>Attack types</h2>
                                                <span>
                                                    {situationPanel.penaltyKillPct == null
                                                        ? 'PK needs opponent EP shots'
                                                        : `Penalty kill ${situationPanel.penaltyKillPct}%`}
                                                </span>
                                            </div>
                                            <table className="insightsPlayerTable insightsTableFit">
                                                <thead>
                                                    <tr>
                                                        <th>Attack</th>
                                                        <th>Our G</th>
                                                        <th>Our Sh</th>
                                                        <th>Ours</th>
                                                        <th>Opp G</th>
                                                        <th>Opp Sh</th>
                                                        <th>Opp</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {situationPanel.rows.map(
                                                        (row: MatchSituationRow) => (
                                                            <tr key={row.type}>
                                                                <td>{row.label}</td>
                                                                <td>{row.us.goals}</td>
                                                                <td>{row.us.shots}</td>
                                                                <td>
                                                                    {row.usPct == null
                                                                        ? '-'
                                                                        : `${row.usPct}%`}
                                                                </td>
                                                                <td>{row.them.goals}</td>
                                                                <td>{row.them.shots}</td>
                                                                <td>
                                                                    {row.themPct == null
                                                                        ? '-'
                                                                        : `${row.themPct}%`}
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </section>
                                    ) : null}
                                </div>
                            ) : null}

                            {periodDetails.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>By period</h2>
                                        <span>Us vs them · shots, exclusions, turnovers</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Period</th>
                                                <th>GF</th>
                                                <th>GA</th>
                                                <th>Sh+</th>
                                                <th>Sh-</th>
                                                <th>Ex+</th>
                                                <th>Ex-</th>
                                                <th>TO+</th>
                                                <th>TO-</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {periodDetails.map((row: MatchPeriodDetail) => (
                                                <tr key={row.periodId}>
                                                    <td>{row.label}</td>
                                                    <td>{row.goalsFor}</td>
                                                    <td>{row.goalsAgainst}</td>
                                                    <td>{row.shotsFor}</td>
                                                    <td>{row.shotsAgainst}</td>
                                                    <td>{row.exclusionsFor}</td>
                                                    <td>{row.exclusionsAgainst}</td>
                                                    <td>{row.turnoversFor}</td>
                                                    <td>{row.turnoversAgainst}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}

                            <TwScorersBoard rows={scorers} cell={rateCell} rateMode="totals" />
                            <TwSavesBoard rows={keepers} cell={rateCell} rateMode="totals" />
                            <TwOtherBoard rows={otherRows} cell={rateCell} />

                            <InsightsMatchMaps
                                bundles={mapBundle}
                                ourTeamIds={perspectiveTeamIds}
                            />

                            <section className="insightsPanel">
                                <div className="insightsPanelHead">
                                    <h2>Event timeline</h2>
                                    <span>Grouped by period · score on goals</span>
                                </div>
                                {timelineGroups.length === 0 ? (
                                    <div className="insightsEmpty">
                                        No events logged for this match yet.
                                    </div>
                                ) : (
                                    <div className="insightsTimelineGroups">
                                        {timelineGroups.map((group) => (
                                            <div
                                                key={group.periodId}
                                                className="insightsTimelineGroup"
                                            >
                                                <div className="insightsTimelineGroupHead">
                                                    {group.periodLabel}
                                                </div>
                                                <ul className="insightsTimeline">
                                                    {group.rows.map((row) => (
                                                        <li
                                                            key={row.event.id}
                                                            className={`insightsTimelineRow is-${row.side}`}
                                                        >
                                                            <span className="insightsTimelineClock">
                                                                {row.clock}
                                                            </span>
                                                            <span className="insightsTimelineLabel">
                                                                {row.label}
                                                                {row.playerLine
                                                                    ? ` · ${row.playerLine}`
                                                                    : ''}
                                                                {row.scoreAt
                                                                    ? ` · ${row.scoreAt}`
                                                                    : ''}
                                                            </span>
                                                            <span className="insightsTimelineSide">
                                                                {row.side === 'home'
                                                                    ? 'Home'
                                                                    : row.side === 'away'
                                                                    ? 'Away'
                                                                    : '-'}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
};

interface ComparisonRow {
    label: string;
    home: string;
    away: string;
    /** Share of the bar taken by the home side, 0-100. */
    homeShare: number;
}

const buildComparisonRows = (home: TeamMatchStats, away: TeamMatchStats): ComparisonRow[] => {
    const share = (a: number, b: number): number =>
        a + b > 0 ? Math.round((a / (a + b)) * 100) : 50;
    const percentText = (value?: number): string => (value == null ? '-' : `${value}%`);

    const homeShooting = shootingPercent(home.goals, home.shots);
    const awayShooting = shootingPercent(away.goals, away.shots);

    return [
        {
            label: 'Goals',
            home: `${home.goals}`,
            away: `${away.goals}`,
            homeShare: share(home.goals, away.goals),
        },
        {
            label: 'Shots',
            home: `${home.shots}`,
            away: `${away.shots}`,
            homeShare: share(home.shots, away.shots),
        },
        {
            label: 'Shooting',
            home: percentText(homeShooting),
            away: percentText(awayShooting),
            homeShare: share(homeShooting || 0, awayShooting || 0),
        },
        {
            label: 'Saves',
            home: `${home.saves}`,
            away: `${away.saves}`,
            homeShare: share(home.saves, away.saves),
        },
        {
            label: 'Exclusions',
            home: `${home.exclusions}`,
            away: `${away.exclusions}`,
            homeShare: share(home.exclusions, away.exclusions),
        },
        {
            label: 'Turnovers',
            home: `${home.turnovers}`,
            away: `${away.turnovers}`,
            homeShare: share(home.turnovers, away.turnovers),
        },
        {
            label: 'Timeouts',
            home: `${home.timeouts}`,
            away: `${away.timeouts}`,
            homeShare: share(home.timeouts, away.timeouts),
        },
    ];
};

const TeamComparison: React.FC<{
    home: TeamMatchStats;
    away: TeamMatchStats;
    homeName: string;
    awayName: string;
}> = ({ home, away, homeName, awayName }) => (
    <div className="insightsCompare">
        <div className="insightsCompareHead">
            <span>{homeName}</span>
            <span />
            <span>{awayName}</span>
        </div>
        {buildComparisonRows(home, away).map((row: ComparisonRow) => (
            <div key={row.label} className="insightsCompareRow">
                <b>{row.home}</b>
                <div className="insightsCompareMid">
                    <i>{row.label}</i>
                    <div className="insightsCompareBar">
                        <span className="isHome" style={{ width: `${row.homeShare}%` }} />
                        <span className="isAway" style={{ width: `${100 - row.homeShare}%` }} />
                    </div>
                </div>
                <b>{row.away}</b>
            </div>
        ))}
    </div>
);

export default InsightsMatch;
